import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { H2, H4 } from '@/@bothrs-ui/web/components/typography/Heading'
import { mixpanel } from '@/mixpanel'
import { serializeHTML } from '@/modules/content/components/RichText/serialize'
import { openUrl } from '@/utils/external-resources'

import type { CareProgram } from '../types/care-program.type'

const CarePathCard = ({ program }: { program: CareProgram }) => {
  const navigate = useNavigate()

  return (
    <Card
      key={program.id}
      onClick={(event) => {
        event.stopPropagation()

        const linkedUrl = program?.linkedEntry?.[0]?.url
        if (linkedUrl) {
          mixpanel.track('care_card_external_click')
          openUrl(linkedUrl)
        } else {
          mixpanel.track('care_card_internal_click')
          navigate(`/care/care-program/${program.id}`)
        }
      }}
    >
      <ImageContainer>
        <Shade />
        <Image src={program.featuredImage?.url} />
        <TagContainer>
          {program.tags.map((tag) => (
            <Tag key={tag.title}>{tag.title}</Tag>
          ))}
        </TagContainer>
        <TitleContainer>
          <Title>{program.title}</Title>
          <Subtitle>{program.description && serializeHTML(program.description)}</Subtitle>
        </TitleContainer>
      </ImageContainer>
    </Card>
  )
}

const Card = styled(FlexColumn)`
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Large}px;
  overflow: hidden;
  position: relative;
  height: 100%; // Ensure card takes full height of grid cell
  cursor: pointer;
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`

const ImageContainer = styled.div`
  position: relative;
  flex: 1;
  min-height: 200px;
  height: 200px;
  max-height: 200px;
  overflow: hidden; // Ensures no overflow beyond 200px height
`

const Image = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover; // Ensures the image fills the container without stretching
  object-position: center; // Centers the image focus
`

const TagContainer = styled(FlexRow)`
  position: absolute;
  top: ${({ theme }) => theme.UI.SpacingPx.Space4};
  right: ${({ theme }) => theme.UI.SpacingPx.Space4};

  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
`

const Tag = styled(FlexColumn)`
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Large}px;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space1}
    ${({ theme }) => theme.UI.SpacingPx.Space2};

  color: ${({ theme }) => theme.Colors.Neutrals.Lightest};
  font-weight: 500;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(2px);
`

const Shade = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;

  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.5) 0%,
    rgba(0, 0, 0, 0.25) 100%
  );
`

const Title = styled(H2)`
  color: ${({ theme }) => theme.Colors.Neutrals.Lightest};
  margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space2};
`

const TitleContainer = styled(FlexColumn)`
  position: absolute;

  bottom: ${({ theme }) => theme.UI.SpacingPx.Space6};
  left: ${({ theme }) => theme.UI.SpacingPx.Space6};
  right: ${({ theme }) => theme.UI.SpacingPx.Space6};
`

const Subtitle = styled(H4)`
  * {
    color: ${({ theme }) => theme.Colors.Neutrals.Lightest};
    font-weight: 500 !important;
    margin: 0;
    font-size: 1rem;
  }

  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2; /* Number of lines to show */
  max-height: calc(1.2em * 3); /* Fallback height for older browsers */
  line-height: 1.2em;
  margin: 0;
`

export { CarePathCard }
